/**
 * What the optional word list shows: every word in the level, and how each
 * one went.
 *
 * Pure: no Phaser. Game reports hits and misses; WordList draws what this
 * returns. Kept separate because the rules have edges worth testing on their
 * own - a spring or jetpack flies past platforms whose words are never typed,
 * and a miss drops the animal a platform whose word is then replaced with a
 * fresh one (replace below).
 */

export interface WordRow {
  /** The platform this word belongs to. */
  readonly index: number;
  readonly word: string;
  /** Wrong attempts, each shown as a small cross. */
  readonly misses: number;
  /** Typed correctly at least once. */
  readonly done: boolean;
  /** Flown past by a power-up without being typed. */
  readonly skipped: boolean;
}

interface MutableRow {
  index: number;
  word: string;
  misses: number;
  done: boolean;
  skipped: boolean;
}

export class WordLog {
  private readonly byIndex = new Map<number, MutableRow>();
  private readonly ordered: MutableRow[] = [];

  /** One row per platform that has a word, in climbing order. */
  constructor(platforms: ReadonlyArray<{ readonly index: number; readonly word?: string }>) {
    for (const platform of platforms) {
      if (platform.word === undefined || platform.word === '') continue;
      const row = {
        index: platform.index,
        word: platform.word,
        misses: 0,
        done: false,
        skipped: false,
      };
      this.byIndex.set(platform.index, row);
      this.ordered.push(row);
    }
    this.ordered.sort((a, b) => a.index - b.index);
  }

  /**
   * The word for `index` was typed correctly and the animal landed on
   * `landing`. Anything between the two was flown past by a power-up.
   */
  hit(index: number, landing: number): void {
    const row = this.byIndex.get(index);
    if (row !== undefined) {
      row.done = true;
      row.skipped = false;
    }
    for (let i = index + 1; i <= landing; i += 1) {
      const passed = this.byIndex.get(i);
      // A word already typed stays ticked; only untouched ones count as skipped.
      if (passed !== undefined && !passed.done) passed.skipped = true;
    }
  }

  /** The word for `index` was typed wrong. */
  miss(index: number): void {
    const row = this.byIndex.get(index);
    if (row !== undefined) row.misses += 1;
  }

  /**
   * Platform `index` has a new word: after a miss, the platform the animal
   * dropped back to is given a fresh one instead of a word already typed.
   */
  replace(index: number, word: string): void {
    const row = this.byIndex.get(index);
    if (row === undefined) return;
    row.word = word;
    row.done = false;
    row.skipped = false;
    row.misses = 0;
  }

  rows(): readonly WordRow[] {
    return this.ordered;
  }

  /** Position in rows() of the word for platform `index`, or -1. */
  positionOf(index: number): number {
    return this.ordered.findIndex((row) => row.index === index);
  }
}

/* ----------------------------------------------------------------- layout */

/** Gap from a word's end to its first mark, and between marks, in px. */
export const MARK_GAP = 9;
/** Crosses beyond this are summarised as "+n" rather than running on. */
export const MAX_CROSSES = 4;
/** Room taken by the "+n" summary. */
export const OVERFLOW_W = 18;
/** How far a tick reaches past the point it is drawn from. */
export const TICK_REACH = 6;
/**
 * The list's padding, in px: from the panel's left edge to the words, and
 * the same again past the longest word on the right, so the box hugs its
 * contents evenly instead of being a fixed width.
 */
export const LIST_PAD = 18;

/**
 * How far past the end of a word its marks reach, in px; 0 when it has none.
 *
 * Mirrors WordList's drawing exactly: crosses every MARK_GAP, then the "+n"
 * if there are more than MAX_CROSSES, then the tick.
 */
export function marksExtent(misses: number, done: boolean): number {
  const crosses = Math.min(Math.max(0, misses), MAX_CROSSES);
  const overflow = misses > MAX_CROSSES;
  const next = MARK_GAP + crosses * MARK_GAP + (overflow ? OVERFLOW_W : 0);
  if (done) return next + TICK_REACH;
  if (overflow) return next - 4;
  return crosses > 0 ? 3 + crosses * MARK_GAP : 0;
}

export interface ListEntry {
  readonly width: number;
  readonly misses: number;
  readonly done: boolean;
}

/**
 * Width of the panel behind the list.
 *
 * The longest word plus LIST_PAD on each side. A tick fits inside that right
 * padding, so the box does not change size as words are finished; it only
 * grows when a word collects enough crosses that its marks would otherwise
 * spill past the edge, keeping a small margin beyond them.
 */
export function listPanelWidth(entries: readonly ListEntry[], pad = LIST_PAD): number {
  let content = 0;
  for (const entry of entries) {
    content = Math.max(
      content,
      entry.width + pad,
      entry.width + marksExtent(entry.misses, entry.done) + 3,
    );
  }
  return pad + content;
}

/**
 * Which rows to draw when the list is longer than the space for it.
 *
 * Keeps the current word `lead` rows from the top, so a few finished words
 * stay visible above it for context while most of the space goes to what is
 * coming - which is the point of the list for a fast typist reading ahead.
 */
export function listWindow(
  total: number,
  current: number,
  visible: number,
  lead = 3,
): { readonly start: number; readonly end: number } {
  if (total <= visible) return { start: 0, end: total };
  const start = Math.min(Math.max(0, current - lead), total - visible);
  return { start, end: start + visible };
}
